import { TextZoom } from '@capacitor/text-zoom';
import { DeviceProvider } from './device';
import { Platform } from '@ionic/angular';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import write_blob from 'capacitor-blob-writer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import mime from 'mime';

export class DeviceMobileProvider extends DeviceProvider
{
    //#region Constructor

    /**
     * Class constructor
     * @param platform
     */
    constructor
        (
            platform: Platform
        )
    {
        super(platform);
    }

    //#endregion


    //#region Methods

    /**
     * Initialize the device environment
     */
    public override async loadDeviceInfo(): Promise<any>
    {
        // Call the parent load device info
        await super.loadDeviceInfo();

        // set the zoom level on the device
        await TextZoom.set({ value: 1 });
    }

    /**
     * Mobile implementation for calling a number
     * @param {string} number
     */
    public async callNumber(number: string)
    {
        window.open(`tel:${number.replace(' ', '')}`, '_system');
    }

    /**
     * Setup the device's required permissions
     */
    public async setupDevicePermissions(): Promise<boolean>
    {
        return Promise.resolve(true);
    }

    /**
     * Open the device's settings
     * @param settings 
     */
    public openSettings(settings: AndroidSettings | IOSSettings)
    {
        // If the device is running Android then 
        // call the plugin's Android settings method
        // If the device is running iOS then
        // call the plugin's iOS settings method
        // Throw exception if invalid
        if (this.isAndroid())
        {
            NativeSettings.openAndroid({
                option: <AndroidSettings>settings,
            });
        } else if (this.isiOS())
        {
            NativeSettings.openIOS({
                option: <IOSSettings>settings,
            });
        } else
        {
            throw Error('OpenSettings method can only be called on iOS and Android!');
        }
    }

    /**
     * Load a file from the device
     * @param filePath file name
     */
    public async loadFile(filePath: string): Promise<File>
    {
        // Read the file from the path given
        let fileResults = await Promise.all([Filesystem.readFile({ path: filePath }), Filesystem.stat({ path: filePath })]);

        // Split the promise result into separate variables
        let result = fileResults[0];
        let statResult = fileResults[1];

        // Get the file name and type from the path given
        let fileName = filePath.split('\\').pop().split('/').pop();
        let fileType = mime.getType(fileName);

        // Convert the path to a File object and return it
        let file = this.dataURLtoFile(`data:${fileType};base64, ${result.data}`, fileName);
        return file;
    }

    /**
     * Convert a data Url to a file. This is done to convert any base64 read files, typically through Capacitor,
     * to a File object similar to one retrieved from a <input type="file" /> element.
     * 
     * Code was adapted from https://stackoverflow.com/questions/35940290/how-to-convert-base64-string-to-javascript-file-object-like-as-from-file-input-f.
     * @param dataurl 
     * @param filename 
     * @returns 
     */
    private dataURLtoFile(dataurl, filename)
    {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[arr.length - 1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--)
        {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    /**
     * Save a file on the device
     * @param fileName 
     * @param blob 
     */
    public async saveFile(fileName: string, blob: Blob)
    {
        // Write the blob to disk
        await write_blob({
            path: fileName,
            directory: Directory.Documents,
            blob: blob,
            fast_mode: true,
            recursive: true
        });

        // Get the newly created file path
        let fileUri = await Filesystem.getUri({ directory: Directory.Documents, path: fileName });

        // Open the file
        await FileOpener.open({ filePath: fileUri.uri });
    }

    //#endregion
}
